import axios from 'axios';
import { apiClient } from './client';
import type { PresignedUploadResponse, PresignedDownloadResponse } from '../types/api';

/**
 * Files API 클라이언트 (P2-17) — chat 첨부 업로드 + 다운로드.
 *
 * Upload:
 *   1) requestPresignedUpload(file) → backend POST /files/presigned-upload → fileId/uploadUrl/fileKey/expiresAt
 *   2) uploadToPresignedUrl(url, file, onProgress) → MinIO 직접 PUT (서버 트래픽 회피)
 *   3) 메시지 send 시 attachments=[{ fileId, name, mimeType, sizeBytes }] 결합
 *
 * Download:
 *   1) requestPresignedDownload(fileId) → backend POST /files/{id}/presigned-download → downloadUrl/name/mimeType
 *   2) browser 가 직접 GET (anchor download 또는 새 탭). 또는 image/* 는 src 로 임베드.
 *
 * 보안: backend 가 mime 화이트리스트 + sizeBytes 제한 + institution scope. presigned URL TTL
 * 5분 (lib/minio.ts SSOT). presigned URL 은 자체 인증 (signature) — apiClient 우회.
 */

export type { PresignedUploadResponse, PresignedDownloadResponse };

export interface RequestPresignedInput {
  filename: string;
  contentType: string;
  sizeBytes: number;
  /**
   * 게시판 첨부일 때만 실린다 (2026-08-19, 선택). 서버가 이 게시판의 소유 스코프
   * (회사·팀·개인)로 용량 한도를 검사하고 files 행에도 같은 스코프를 찍는다.
   * 안 보내면 종전대로 업로더 개인 용량에서 나간다 — 채팅·메일·캘린더는 무변경.
   * ⚠ 서버 스키마가 additionalProperties:false 라 **옛 chat-server 는 400** 을 낸다.
   *   배포는 반드시 chat-server 를 먼저 올린 뒤 프론트.
   */
  boardId?: string;
}

export async function requestPresignedUpload(input: RequestPresignedInput): Promise<PresignedUploadResponse> {
  const { data } = await apiClient.post<PresignedUploadResponse>('/files/presigned-upload', input);
  return data;
}

/**
 * chat-inline-media-playback (2026-07-12) — disposition 옵션 추가(additive).
 *   미지정 → 기존 동작(Content-Disposition:attachment 강제, 다운로드/이미지 임베드).
 *   'inline' → 서버가 attachment override 없는 순수 presigned GET 발급(비-active-content 한정).
 *     <audio>/<video src> 스트리밍용 — MinIO Range(206) 로 seek·프로그레시브 재생.
 */
export async function requestPresignedDownload(
  fileId: string,
  /**
   * `purpose` (2026-09-14 · 계약 §3.4.1 · 서버 #1268) — 감사 이름표만 가른다(권한 · 주소 · 만료 같음).
   *   `'preview'` = 「열람」(`file.preview_presigned`) — 썸네일 · 임베드 · 인라인 재생 · 전체화면 보기 · 인앱 뷰어/편집기.
   *   미지정 · `'download'` = 「내려받음」(`file.download_presigned`, 종전) — 기기에 파일이 생기는 저장 · 내려받기 · 새 탭 열기.
   *   ★ 가르는 기준은 「기기에 저장하느냐」. `disposition` 과 따로 논다. 옛 서버는 모르는 칸을 지우고 종전대로 남긴다.
   */
  opts?: { disposition?: 'inline' | 'attachment'; purpose?: 'preview' | 'download' },
): Promise<PresignedDownloadResponse> {
  const params = new URLSearchParams();
  if (opts?.disposition) params.set('disposition', opts.disposition);
  if (opts?.purpose) params.set('purpose', opts.purpose);
  const query = params.toString() ? `?${params.toString()}` : '';
  const { data } = await apiClient.post<PresignedDownloadResponse>(`/files/${fileId}/presigned-download${query}`);
  return data;
}

interface FinalizeResponse {
  fileId: string;
  fileKey: string;
  verified: boolean;
  sizeBytes: number;
}

/**
 * MinIO PUT 성공 후 backend 통보 — File row 의 'pending:' 접두사 제거 + statObject + magic byte 검증.
 *
 * 응답:
 *   - 200 verified=true: 정상 finalize. backend 가 실재·size·magic byte 모두 검증.
 *   - 200 verified=false: backend MinIO fallback 환경 (dev/CI) — 검증 skip 됐으나 storagePath 갱신.
 *   - 422 SIZE_MISMATCH / MIME_MISMATCH: 검증 실패 — file row soft delete 됨, 사용자에게 안내 필요.
 *   - 409 FILE_NOT_PENDING: 이미 finalize 됐거나 잘못된 상태.
 *   - 404 FILE_NOT_FOUND: ownership 없음.
 */
export async function finalizeUpload(fileId: string): Promise<FinalizeResponse> {
  const { data } = await apiClient.post<FinalizeResponse>(`/files/${fileId}/finalize`);
  return data;
}

export interface UploadProgressEvent {
  loaded: number;
  total: number | undefined;
  percent: number;
}

export interface UploadOptions {
  signal?: AbortSignal;
  onProgress?: (ev: UploadProgressEvent) => void;
  /** 게시판 첨부의 용량 귀속 (2026-08-19). 미지정이면 종전대로 개인 용량. */
  boardId?: string;
}

/**
 * MinIO 에 PUT — apiClient (axios with auth) 우회. presigned URL 은 자체 인증 (signature).
 * Authorization 헤더 송신 시 MinIO 가 거부하므로 raw axios 사용.
 *
 * Content-Type 은 presigned 발급 시 합의된 값과 동일해야 — signature 일부.
 */
export async function uploadToPresignedUrl(
  uploadUrl: string,
  file: File | Blob,
  contentType: string,
  options: UploadOptions = {},
): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': contentType },
    signal: options.signal,
    onUploadProgress: (ev) => {
      if (!options.onProgress) return;
      const total = ev.total;
      const percent = total ? Math.round((ev.loaded / total) * 100) : 0;
      options.onProgress({ loaded: ev.loaded, total, percent });
    },
  });
}

/**
 * 일괄 단순 업로드 — File → presign → PUT → ChatMessage attachments 항목 반환.
 */
export interface UploadedAttachment {
  fileId: string;
  /**
   * 스토리지 키 — 올려서 만든 첨부에만 있다.
   *
   * ★ 2026-08-22 선택으로 바꿨다. 워크스페이스에 **이미 있는 파일**을 고를 때는 그 파일을
   *   다시 올리지 않으므로 키를 알 길이 없다(목록 응답에 없다). 서버는 `fileId` 로만 검증하고
   *   (mail-helpers 의 checkMailAttachmentsAgainstFiles), 이 값을 읽는 곳도 없다 —
   *   있는 척 빈 문자열을 적어 두는 것보다 없는 것이 사실이다.
   */
  fileKey?: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
}

// chat-html-preview (2026-07-18, iOS #230 / AOS #231 웹 파리티) — HTML 첨부 업로드 안전화.
// 서버 presign 화이트리스트가 stored-XSS 방지로 text/html 을 차단하므로, HTML(.html/.htm/.xhtml)은
// 바이트 불변인 채 content-type 을 안전한 text/plain 으로 declare 해 통과시킨다(모바일과 동일). 파일명
// 확장자는 보존 → 수신측이 확장자로 html 미리보기 판정. 서버는 Content-Disposition:attachment 로 서빙
// 하므로 inline HTML 실행 없음(XSS 회피). 미리보기는 sandboxed iframe(스크립트 미실행)으로만 렌더.
const HTML_UPLOAD_EXT_RE = /\.(html?|xhtml)$/i;

// chat-archive-attachment (2026-07-27) — 압축파일(.zip/.7z/.rar/.tar/.gz…) 첨부 지원.
// 브라우저는 .7z/.rar/.xz 등에 file.type 을 빈 문자열로 주고(→ octet-stream, 서버 화이트리스트 차단),
// .zip 도 OS 마다 변종(application/x-zip-compressed)을 준다. 확장자로 **정규 MIME 을 고정**해
// declare 한다 — 서버 SSOT = chat-server files.routes.ts ALLOWED_ARCHIVE_MIMES.
// (바이트는 불변. 서버는 Content-Disposition:attachment 로 서빙하므로 inline 실행 여지 없음.)
const ARCHIVE_EXT_MIME: Record<string, string> = {
  zip: 'application/zip',
  '7z': 'application/x-7z-compressed',
  rar: 'application/vnd.rar',
  tar: 'application/x-tar',
  gz: 'application/gzip',
  tgz: 'application/gzip',
  bz2: 'application/x-bzip2',
  tbz2: 'application/x-bzip2',
  xz: 'application/x-xz',
  txz: 'application/x-xz',
};

// workspace-hwp (2026-08-14) — 한컴 한글 문서(.hwp/.hwpx). 압축파일과 **같은 이유·같은 방식**이다:
// 브라우저가 이 둘에 file.type 을 빈 문자열로 주는 일이 흔해 octet-stream 으로 떨어지고,
// 서버 화이트리스트에 걸려 415 가 났다. 확장자로 정규 MIME 을 고정해 declare 한다 —
// 서버 SSOT = chat-server files.routes.ts ALLOWED_DOCUMENT_MIMES. (바이트는 불변.)
const DOC_EXT_MIME: Record<string, string> = {
  hwp: 'application/x-hwp',
  hwpx: 'application/hwp+zip',
};

/** 파일명 확장자(소문자). 없으면 null. */
function fileExt(filename: string): string | null {
  const i = filename.lastIndexOf('.');
  if (i < 0 || i === filename.length - 1) return null;
  return filename.slice(i + 1).toLowerCase();
}

/** 파일명 확장자 → 압축 정규 MIME. 압축이 아니면 null. */
export function archiveUploadContentType(filename: string): string | null {
  const ext = fileExt(filename);
  return ext ? ARCHIVE_EXT_MIME[ext] ?? null : null;
}

/** 파일명 확장자 → 한글 문서 정규 MIME. 한글 문서가 아니면 null. */
export function documentUploadContentType(filename: string): string | null {
  const ext = fileExt(filename);
  return ext ? DOC_EXT_MIME[ext] ?? null : null;
}

export function safeUploadContentType(file: File): string {
  const declared = file.type || 'application/octet-stream';
  if (HTML_UPLOAD_EXT_RE.test(file.name) || declared === 'text/html' || declared.startsWith('application/xhtml')) {
    return 'text/plain';
  }
  const archive = archiveUploadContentType(file.name);
  if (archive) return archive;
  const doc = documentUploadContentType(file.name);
  if (doc) return doc;
  return declared;
}

/** 서버 SSOT(chat-server files.routes.ts MAX_SIZE_BYTES) 와 동일한 첨부 상한. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/**
 * chat-archive-attachment (2026-07-27) — 업로드 실패 메시지 사람 말로 변환(additive).
 * 종전엔 axios 원문("Request failed with status code 415")이 그대로 칩 아래 노출됐다.
 * 압축파일은 용량 초과·형식 거부가 상대적으로 잦아 원인을 알려준다.
 */
function toUploadError(err: unknown): Error {
  const res = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response;
  const status = res?.status;
  if (status === 415) return new Error('지원하지 않는 파일 형식입니다');
  if (status === 400) return new Error(`파일이 너무 큽니다 (최대 ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB)`);
  // 2026-08-19 — 용량 초과·게시판 권한은 서버 문구를 그대로 쓴다. 서버만이 어느 용량
  // (개인·팀·회사)에서 얼마가 남았는지 알고, 여기서 다시 지어내면 두 벌로 갈라진다.
  if ((status === 413 || status === 403) && typeof res?.data?.message === 'string') {
    return new Error(res.data.message);
  }
  return err instanceof Error ? err : new Error('upload_failed');
}

export async function uploadChatAttachment(file: File, options: UploadOptions = {}): Promise<UploadedAttachment> {
  const contentType = safeUploadContentType(file);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`파일이 너무 큽니다 (최대 ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB)`);
  }
  const base = { filename: file.name, contentType, sizeBytes: file.size };
  const presigned = await requestPresignedUpload(
    // 없으면 키 자체를 안 싣는다 — undefined 를 그대로 보내면 JSON 에서 사라지지만
    // 의도를 분명히 하려고 조건부로 편다.
    options.boardId ? { ...base, boardId: options.boardId } : base,
  ).catch(async (err: unknown) => {
    // ⚠ 배포 순서 안전장치 (2026-08-19). 서버 presign 스키마는 additionalProperties:false 라
    //   **boardId 를 모르는 옛 chat-server 는 400** 을 낸다. 그때 첨부가 통째로 막히면
    //   "프론트만 먼저 나가면 게시판이 깨지는" 배포 결합이 생긴다 — main 이 언제든 배포
    //   가능한 기준선이어야 하므로, 한 번만 boardId 없이 다시 시도해 종전 동작(개인 청구)
    //   으로 눕는다. 용량 귀속은 글 저장 때 서버가 다시 맞춘다(stampBoardAttachmentScope).
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (options.boardId && status === 400) {
      return requestPresignedUpload(base).catch((err2: unknown) => {
        throw toUploadError(err2);
      });
    }
    throw toUploadError(err);
  });
  await uploadToPresignedUrl(presigned.uploadUrl, file, contentType, options);
  // PUT 성공 후 finalize — backend 가 statObject + magic byte 검증 + storagePath 갱신.
  // 실패해도 throw — caller 가 사용자에게 검증 실패 안내 (size/mime mismatch 등).
  await finalizeUpload(presigned.fileId);
  return {
    fileId: presigned.fileId,
    fileKey: presigned.fileKey,
    name: file.name,
    mimeType: contentType,
    sizeBytes: file.size,
  };
}
